import React, { CSSProperties } from 'react'
import { Col, Divider, Row, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import {
  ConsensusSituation,
  Request,
  RequestCustomData,
  RequestUserData
} from 'src/types'
import { OriginRequest } from 'src/core/store/ducks/requests/types'
import UserAvatar, { AvatarColorFlag } from 'src/components/UserAvatar'

import * as CSS from './styles'

interface Props {
  bodyStyle?: CSSProperties
  originRequest: OriginRequest
  request: Request
}

function getColorFlag(
  origin: OriginRequest,
  userData: RequestUserData
): AvatarColorFlag {
  switch (origin) {
    case 'my_requests':
      if (
        [
          'APPROVAL_REPROVED',
          'CONFERENCE_REPROVED',
          'CLOSING_REPROVED'
        ].includes(userData.status)
      ) {
        return AvatarColorFlag.RED
      }

      if (['FINISHED'].includes(userData.status)) {
        return AvatarColorFlag.GREEN
      }
      return null

    case 'approvals':
      const isApprovalsConsensus =
        userData.consensus_situation !== ConsensusSituation.REGULAR_APPROVAL

      const approvalsStatus = userData.request_approvals[0]?.status

      if (!isApprovalsConsensus && ['UNDERWAY'].includes(userData.status)) {
        return AvatarColorFlag.GREEN
      }

      if (isApprovalsConsensus) {
        switch (approvalsStatus) {
          case 'APPROVED':
            return AvatarColorFlag.GREEN
          case 'CANCELED':
          case 'EXPIRED':
          case 'REPROVED':
            return AvatarColorFlag.RED
          case 'PENDING':
            return AvatarColorFlag.YELLOW
        }
      }
      return null

    case 'conferences':
      const isConferencesConsensus =
        userData.consensus_situation !== ConsensusSituation.REGULAR_CONFERENCE

      const conferencesStatus = userData.request_conferences[0]?.status

      if (
        !isConferencesConsensus &&
        ['CLOSING_PENDING'].includes(userData.status)
      ) {
        return AvatarColorFlag.GREEN
      }

      if (isConferencesConsensus) {
        switch (conferencesStatus) {
          case 'APPROVED':
            return AvatarColorFlag.GREEN
          case 'CANCELED':
          case 'EXPIRED':
          case 'REPROVED':
            return AvatarColorFlag.RED
          case 'PENDING':
            return AvatarColorFlag.YELLOW
        }
      }
      return null

    case 'closings':
      const isClosingsConsensus =
        userData.consensus_situation !== ConsensusSituation.REGULAR_CLOSING

      const closingsStatus = userData.request_closings[0]?.status

      if (!isClosingsConsensus && ['FINISHED'].includes(userData.status)) {
        return AvatarColorFlag.GREEN
      }

      if (isClosingsConsensus) {
        switch (closingsStatus) {
          case 'APPROVED':
            return AvatarColorFlag.GREEN
          case 'CANCELED':
          case 'EXPIRED':
          case 'REPROVED':
            return AvatarColorFlag.RED
          case 'PENDING':
            return AvatarColorFlag.YELLOW
          default:
            return null
        }
      }
      return null
    default:
      return null
  }
}

function handleRenderRowCustomData(
  item: RequestCustomData,
  t: (t: string) => string
) {
  let contractName
  let targetName

  if (!!item.contract && !!item.contract.name) {
    contractName = `${item.contract.code} - ${item.contract.name}`
  }

  if (!!item.target && !!item.target.name) {
    targetName = `${item.target.code} - ${item.target.name}`
  }

  return (
    <CSS.RowCustomData key={item.request_custom_data_id || item.tmp_id}>
      {!!targetName && (
        <Col span={12}>
          <Typography.Text strong>
            {t('web.assignment.billing.form.target.title')}
          </Typography.Text>
          {': '}
          {targetName}
        </Col>
      )}
      {!!contractName && (
        <Col offset={targetName ? 1 : 0} span={targetName ? 11 : 12}>
          <Typography.Text strong>
            {t('web.assignment.sharing.form.contract.title')}
          </Typography.Text>
          {': '}
          {contractName}
        </Col>
      )}
    </CSS.RowCustomData>
  )
}

export default function SetupSummary({
  bodyStyle = {},
  request,
  originRequest
}: Props): JSX.Element {
  const { t } = useTranslation()
  return (
    <CSS.Card
      bodyStyle={bodyStyle}
      data-test-id="summary-setup"
      title="web.setup.title-summary"
    >
      <Row>
        <Col span={12}>
          <Row>
            <Typography.Text strong>
              {t('web.setup.form.field-title.title')}
              {': '}
            </Typography.Text>
            {request.title}
          </Row>
          <Row>
            <Typography.Text strong>
              {t('web.setup.form.start-date.title')}
              {': '}
            </Typography.Text>
            {`${moment
              .unix(request.requested_on)
              .format('DD/MM/YYYY')} - ${moment
              .unix(request.date_end)
              .format('DD/MM/YYYY')}`}
          </Row>
          <Row>
            <Typography.Text strong>
              {t('web.setup.form.reason.title')}
              {': '}
            </Typography.Text>
            {request.request_user_data[0]?.reason?.name}
          </Row>
        </Col>
        <Col offset={1} span={11}>
          <Row>
            <Typography.Text strong>
              {t('web.setup.participants.title')}
            </Typography.Text>
          </Row>
          {request.request_user_data.map((item, index) => (
            <Tooltip key={index} title={item.traveler.name}>
              <CSS.ContainerUserAvatar>
                <UserAvatar
                  colorFlag={getColorFlag(originRequest, item)}
                  gender={item.traveler.gender}
                  name={`${item.traveler.name}`}
                  size="large"
                />
              </CSS.ContainerUserAvatar>
            </Tooltip>
          ))}
        </Col>
      </Row>
      {!!request.request_custom_data.length && <Divider />}
      {request.request_custom_data.map(item =>
        handleRenderRowCustomData(item, t)
      )}
    </CSS.Card>
  )
}
